import React from 'react'
import styled from 'styled-components'

import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import {
  margin,
  marginSizes,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import VisibilitySensor from '@jarvis.oddle/core/components/VisibilitySensor'
import { H1, H3 } from '@jarvis.oddle/core/components/Typo'

import ResponsiveSection from 'src/components/core/ResponsiveSection'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectPreviewMode, selectSections } from 'src/redux/customisation'
import { DEFAULT_BANNER_IMAGE_URL } from 'config/client'
import breakpoint from 'src/utils/styledComponentsBreakpoint'

import Button from './LinkButton'
import { getColorString } from 'src/utils/style'

const StyledResponsiveSection = styled(ResponsiveSection)<{ index: number }>`
  margin-bottom: 30px;
  margin-top: 30px;
  ${props => props.index === 0 || props.index === 1 ? `
    margin-top: 0;
  ` : ''}
`

const ResponsiveHeightContainer = styled(Div)<{ bannerHeight: string }>`
  position: initial;
  width: 100%;
  top: 0;
  padding: 0;
  display: flex;
  min-height: ${(props) =>
    props.bannerHeight === 'large'
      ? '100vh'
      : props.bannerHeight === 'medium'
      ? '70vh'
      : '50vh'};
`

const BackgroundImage = styled(Div)<{ src: string; bannerHeight: string }>`
  min-height: ${(props) =>
    props.bannerHeight === 'large'
      ? '100vh'
      : props.bannerHeight === 'medium'
      ? '70vh'
      : '50vh'};
  width: 100%;
  background-image: url('${(props) => props.src || DEFAULT_BANNER_IMAGE_URL}');
  background-size: cover;
  background-position: center;
`

const OverlayBackgroundImage = styled(Div)<{
  coverOverlayColour: string
  backgroundImage: string
}>`
  height: 100%;
  width: 100%;
  background: ${(props) => props.coverOverlayColour ? getColorString(props.coverOverlayColour) : 'rgba(0, 0, 0, 0.3)'};
`

const StyledWrapperContent = styled(Div)<{ stuck: boolean }>`
  display: block;
  transition: all 0.5s ease-in-out;
  transform: ${(props) => (props.stuck ? 'translateY(50%)' : 'translateY(0)')};
  opacity: ${(props) => (props.stuck ? 0 : 1)};
`

const StyledTextHeader = styled(H1)<{ textColour: string }>`
  font-weight: 700;
  width: 80vw;
  color: ${(props) => props.textColour || '#fff'};
  padding: 10px 0;
  max-width: 960px;
  text-align: center;
  margin: auto;
  line-height: 1.875rem;
  ${breakpoint('tablet')`
    font-size: 44px;
    line-height: 2.375rem;
  `}
`

const StyledTextSubHeader = styled(H3)<{ textColour: string }>`
  width: 80vw;
  font-weight: 400;
  color: ${(props) => props.textColour || '#fff'};
  padding: 10px 0;
  max-width: 960px;
  text-align: center;
  margin: auto;
  line-height: 1.5rem;
  font-family: ${(props) => props.theme.typographyBodyText} !important;
  ${breakpoint('tablet')`
    font-size: 32px;
    line-height: 2.375rem;
  `}
`

const StyledButtonSection = styled(Flex)`
  width: 100%;
  @media (max-width: 720px) {
    margin-top: ${margin[marginSizes.small]};
    flex-flow: wrap;
  }
`

interface WideBannerSection {
  id: string
  name: string
  type: string
  settings: {
    backgroundImage: string
    bannerHeight: string
    headerText: string
    subheadingsText: string
    textColour: string
    buttonLabel1: string
    buttonLink1: string
    buttonLabel2: string
    buttonLink2: string
    buttonTextColour: string
    buttonColour: string
    coverOverlayColour: string
  }
  updatedAt: string
}

interface WideBannerProps {
  id: string
  index: number
}

const WideBanner = ({ id, index }: WideBannerProps): JSX.Element => {
  const previewMode = useAppSelector(selectPreviewMode)
  const { settings } = useAppSelector(selectSections)[id] as WideBannerSection
  const { t } = React.useContext(TranslationContext)
  const [stuck, setStuck] = React.useState(null)

  const {
    backgroundImage,
    bannerHeight,
    headerText,
    subheadingsText,
    textColour,
    buttonLabel1,
    buttonLink1,
    buttonLabel2,
    buttonLink2,
    buttonTextColour,
    buttonColour,
    coverOverlayColour
  } = settings || {}

  const haveValidButton =
    (buttonLabel1 && buttonLink1) || (buttonLabel2 && buttonLink2)

  return (
    <StyledResponsiveSection index={index}>
      <ResponsiveHeightContainer bannerHeight={bannerHeight}>
        <BackgroundImage src={backgroundImage} bannerHeight={bannerHeight}>
          <OverlayBackgroundImage
            backgroundImage={backgroundImage}
            coverOverlayColour={coverOverlayColour}
          >
            <Flex
              justifyContent='center'
              alignItems='center'
              height='100%'
              flexFlow='row wrap'
              padding={paddingSizes.large}
            >
              <VisibilitySensor onChange={(visible) => setStuck(!visible)}>
                <StyledWrapperContent stuck={stuck}>
                  <StyledTextHeader textColour={textColour}>
                    {headerText || t('Wide banner')}
                  </StyledTextHeader>
                  {subheadingsText && (
                    <StyledTextSubHeader textColour={textColour}>
                      {subheadingsText}
                    </StyledTextSubHeader>
                  )}
                  {haveValidButton ? (
                    <StyledButtonSection justifyContent='center'>
                      <Button
                        href={buttonLink1}
                        previewMode={previewMode}
                        color={buttonTextColour}
                        backgroundColor={buttonColour}
                      >
                        {t(buttonLabel1)}
                      </Button>
                      {buttonLabel2 && buttonLink2 ? (
                        <Button
                          href={buttonLink2}
                          previewMode={previewMode}
                          color={buttonTextColour}
                          backgroundColor={buttonColour}
                        >
                          {t(buttonLabel2)}
                        </Button>
                      ) : null}
                    </StyledButtonSection>
                  ) : null}
                </StyledWrapperContent>
              </VisibilitySensor>
            </Flex>
          </OverlayBackgroundImage>
        </BackgroundImage>
      </ResponsiveHeightContainer>
    </StyledResponsiveSection>
  )
}

export default WideBanner
